import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '../../elements/icon';
import { Accordion } from '../../elements/Accordion';
import CustomLink from '../../CustomLink';
import ExpertSection from '../../Customer/components/ExpertSection';
import PractitionerBadge from '../../Customer/components/PractitionerBadge';
import ReviewsMobile from '../../Customer/components/ReviewsMobile';
import MillionCombinations from '../../Home/MillionCombinations';
import IngredientsSlider from '../../Home/Slider/IngredientsSlider';
import DiscountBanner from '../../Home/DiscountBanner';
import Header from '../../../Layout/Header';
import { LP_LANGUAGE_OPTIONS } from '../../../utils/language';
import useReviewStats from '../../../hooks/useReviewStats';
import { ReviewRating } from '../../../Constants';
import useMeta from '../../../hooks/useMeta';
import { logo_home } from '../../../Data/Images/Home';
import '../../../assets/css/landing-pages.css';
import HeroImage from '../../../assets/guessworklanding.webp';
import RiisePouch from '../../../assets/yellow-paper-pouch-granules.webp';
import Preview1 from '../../../assets/1-preview.webp';
import Preview3 from '../../../assets/3-preview.webp';
import Preview8 from '../../../assets/8-preview.webp';
import FabienneImg from '../../../assets/testimonials/Fabienne.webp';

// ─────────────────────────────────────────────────────────────────────────
// Forked from src/Components/pages/PillsOrSachet/index.jsx for the
// "AI — Removing the guesswork" Meta ad set. Original page is untouched.
// Angle shift: consolidation ("8 pills → 1 pouch") → certainty
// ("you don't know if it's right → now you do"). Same skeleton, same
// components, same brand tokens — copy replaced per section.
// ─────────────────────────────────────────────────────────────────────────

const K = {
  HeroH1Part1:         'LP_Guess_HeroH1Part1',
  HeroH1Part2:         'LP_Guess_HeroH1Part2',
  HeroSubline:         'LP_Guess_HeroSubline',
  HeroCta:             'LP_Guess_HeroCta',
  HeroSocialProof:     'LP_Guess_HeroSocialProof',
  CtaSub:              'LP_Guess_CtaSub',
  TrustLine1:          'LP_Guess_TrustLine1',
  TrustLine2:          'LP_Guess_TrustLine2',
  TickerQuote1:        'LP_Guess_TickerQuote1',
  TickerQuote2:        'LP_Guess_TickerQuote2',
  TickerQuote3:        'LP_Guess_TickerQuote3',
  TickerQuote4:        'LP_Guess_TickerQuote4',
  Stat1Val:            'LP_Guess_Stat1Val',
  Stat1Label:          'LP_Guess_Stat1Label',
  Stat2Val:            'LP_Guess_Stat2Val',
  Stat2Label:          'LP_Guess_Stat2Label',
  Stat3Val:            'LP_Guess_Stat3Val',
  Stat3Label:          'LP_Guess_Stat3Label',
  Stat4Val:            'LP_Guess_Stat4Val',
  Stat4Label:          'LP_Guess_Stat4Label',
  ProbSectionLabel:    'LP_Guess_ProbSectionLabel',
  ProbH2Line1:         'LP_Guess_ProbH2Line1',
  ProbH2Line2:         'LP_Guess_ProbH2Line2',
  ProbSubline:         'LP_Guess_ProbSubline',
  OldWayTitle:         'LP_Guess_OldWayTitle',
  OldWay1:             'LP_Guess_OldWay1',
  OldWay2:             'LP_Guess_OldWay2',
  OldWay3:             'LP_Guess_OldWay3',
  OldWay4:             'LP_Guess_OldWay4',
  OldWay5:             'LP_Guess_OldWay5',
  NewWayTitle:         'LP_Guess_NewWayTitle',
  NewWay1:             'LP_Guess_NewWay1',
  NewWay2:             'LP_Guess_NewWay2',
  NewWay3:             'LP_Guess_NewWay3',
  NewWay4:             'LP_Guess_NewWay4',
  NewWay5:             'LP_Guess_NewWay5',
  ExpertHeading:       'LP_Guess_ExpertHeading',
  ResultsSectionLabel: 'LP_Guess_ResultsSectionLabel',
  ResultsH1:           'LP_Guess_ResultsH1',
  ResultsH2:           'LP_Guess_ResultsH2',
  ResultsStat1Val:     'LP_Guess_ResultsStat1Val',
  ResultsStat1Desc:    'LP_Guess_ResultsStat1Desc',
  ResultsStat2Val:     'LP_Guess_ResultsStat2Val',
  ResultsStat2Desc:    'LP_Guess_ResultsStat2Desc',
  ResultsSource:       'LP_Guess_ResultsSource',
  IngrHeading:         'LP_Guess_IngrHeading',
  IngrSubline:         'LP_Guess_IngrSubline',
  HowWorksHeading:     'LP_Guess_HowWorksHeading',
  Step1Title:          'LP_Guess_Step1Title',
  Step1Desc:           'LP_Guess_Step1Desc',
  Step2Title:          'LP_Guess_Step2Title',
  Step2Desc:           'LP_Guess_Step2Desc',
  Step3Title:          'LP_Guess_Step3Title',
  Step3Desc:           'LP_Guess_Step3Desc',
  FeatQuote:           'LP_Guess_FeatQuote',
  FeatAuthor:          'LP_Guess_FeatAuthor',
  GridHeading:         'LP_Guess_GridHeading',
  GridTagKnow:         'LP_Guess_GridTagKnow',
  GridTagClarity:      'LP_Guess_GridTagClarity',
  GridTagTrust:        'LP_Guess_GridTagTrust',
  GridTagConsistency:  'LP_Guess_GridTagConsistency',
  GridTagSecurity:     'LP_Guess_GridTagSecurity',
  ComparisonHeading:   'LP_Guess_ComparisonHeading',
  CompFeature:         'LP_Guess_CompFeature',
  CompRow1:            'LP_Guess_CompRow1',
  CompRow1Other:       'LP_Guess_CompRow1Other',
  CompRow2:            'LP_Guess_CompRow2',
  CompRow2Other:       'LP_Guess_CompRow2Other',
  CompRow3:            'LP_Guess_CompRow3',
  CompRow3Other:       'LP_Guess_CompRow3Other',
  CompRow4:            'LP_Guess_CompRow4',
  CompRow4Other:       'LP_Guess_CompRow4Other',
  FaqHeading:          'LP_Guess_FaqHeading',
  Faq1Q:               'LP_Guess_Faq1Q',
  Faq1A:               'LP_Guess_Faq1A',
  Faq2Q:               'LP_Guess_Faq2Q',
  Faq2A:               'LP_Guess_Faq2A',
  Faq3Q:               'LP_Guess_Faq3Q',
  Faq3A:               'LP_Guess_Faq3A',
  Faq4Q:               'LP_Guess_Faq4Q',
  Faq4A:               'LP_Guess_Faq4A',
  Faq5Q:               'LP_Guess_Faq5Q',
  Faq5A:               'LP_Guess_Faq5A',
  FinalHeading:        'LP_Guess_FinalHeading',
  FinalSubline:        'LP_Guess_FinalSubline',
  ReviewsHeading:      'LP_Guess_ReviewsHeading',
  StickyLine1:         'LP_Guess_StickyLine1',
  StickyLine2:         'LP_Guess_StickyLine2',
  FoodNote:            'LP_Guess_FoodNote',
  AdDisclosure:        'LP_Pills_AdDisclosure',
};

const SERIF = { fontFamily: "'DM Serif Display', serif" };
const SANS  = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const CtaButton = ({ children, full = true, fontSize = 16 }) => {
  const { metaEvent } = useMeta();
  return (
  <CustomLink
    to="/quiz"
    onClick={() => metaEvent('Lead')}
    className="lp-btn-cta"
    style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, background: '#F6E147', color: '#103642',
      fontWeight: 700, fontSize, borderRadius: 999,
      padding: '18px 36px', textDecoration: 'none',
      width: full ? '100%' : 'auto' }}
  >
    {children}
    <span aria-hidden="true">→</span>
  </CustomLink>
  );
};

// Matches the Home page hero CTA exactly: same colors, radius, padding
// and pulse animation, and the same "HeroCtaShort" copy Home uses.
const HomeCtaButton = ({ t, full = true }) => {
  const { metaEvent } = useMeta();
  return (
    <>
      <style>{`
        @keyframes sgHeroPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(246, 225, 71, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(246, 225, 71, 0); }
        }
        .sg-hero-cta { animation: sgHeroPulse 2s ease-in-out infinite; }
        .sg-hero-cta:active { transform: scale(0.97); }
      `}</style>
      <CustomLink
        to="/quiz"
        onClick={() => metaEvent('Lead')}
        className="sg-hero-cta"
        style={{ ...SANS, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, background: '#F9E04F', color: '#005D67',
          fontWeight: 700, fontSize: 18, borderRadius: 8, whiteSpace: 'nowrap',
          padding: '7px 22px', textDecoration: 'none',
          width: full ? '100%' : 'auto', maxWidth: full ? 340 : undefined }}
      >
        {t('HeroCtaShort')}
        <span aria-hidden="true">→</span>
      </CustomLink>
    </>
  );
};

const Label = ({ children, pill = false }) => {
  const base = {
    ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
    textTransform: 'uppercase', color: '#116D6E',
    display: 'block', textAlign: 'center', margin: 0,
  };
  if (pill) return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ ...base, background: 'rgba(17,109,110,0.1)', borderRadius: 999, padding: '6px 16px' }}>
        {children}
      </span>
    </div>
  );
  return <p style={base}>{children}</p>;
};

const StepNumber = ({ n }) => (
  <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
    background: '#103642', color: '#F6E147', ...SANS,
    fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {n}
  </div>
);

const StarRow = ({ count = 5 }) => (
  <div style={{ display: 'flex', gap: 4 }}>
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} width="18" height="18" viewBox="0 0 20 20" fill="#F6E147">
        <path d="M10 1l2.39 4.84L18 6.75l-4 3.9.94 5.5L10 13.77l-4.94 2.38L6 10.65 2 6.75l5.61-.91L10 1z" />
      </svg>
    ))}
  </div>
);

// Same visual treatment as PractitionerBadge, but with copy scoped to the
// hero on this page only — does not touch the shared component or its
// other usages on this or other pages.
const HeroExpertBadge = ({ t }) => (
  <div style={{
    border: '1px solid rgba(16,54,66,0.15)', borderRadius: 10,
    padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
      <svg width="11" height="24" viewBox="0 0 16 34" fill="none" aria-hidden="true">
        <path d="M9 33 C8.5 26 8 18 9 3" stroke="#103642" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.35"/>
        <path d="M9 29 C5 27 1 22 3 17 C6 19 9 24 9 29Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 22 C5 20 1 15 3 10 C6 12 9 17 9 22Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 15 C5 13 2 8 4 4 C7 6 9 11 9 15Z" fill="#103642" fillOpacity="0.55"/>
        <path d="M9 8 C7 7 5 4 7 2 C8 4 9 6 9 8Z" fill="#103642" fillOpacity="0.5"/>
      </svg>
      <div style={{ textAlign: 'center' }}>
        <p style={{ ...SANS, fontSize: 8, fontWeight: 800, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: '#103642', margin: 0, lineHeight: 1.3 }}>
          {t('LP_BadgeLabelLine1')}
        </p>
        <p style={{ ...SANS, fontSize: 8, fontWeight: 800, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: '#103642', margin: 0, lineHeight: 1.3 }}>
          {t('LP_BadgeLabelLine2')}
        </p>
      </div>
      <svg width="11" height="24" viewBox="0 0 16 34" fill="none" aria-hidden="true" style={{ transform: 'scaleX(-1)' }}>
        <path d="M9 33 C8.5 26 8 18 9 3" stroke="#103642" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.35"/>
        <path d="M9 29 C5 27 1 22 3 17 C6 19 9 24 9 29Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 22 C5 20 1 15 3 10 C6 12 9 17 9 22Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 15 C5 13 2 8 4 4 C7 6 9 11 9 15Z" fill="#103642" fillOpacity="0.55"/>
        <path d="M9 8 C7 7 5 4 7 2 C8 4 9 6 9 8Z" fill="#103642" fillOpacity="0.5"/>
      </svg>
    </div>
    <div style={{ width: '1px', height: 26, background: 'rgba(16,54,66,0.15)', flexShrink: 0 }} />
    <p style={{ ...SANS, fontSize: 11, fontWeight: 600, color: '#103642',
      margin: 0, lineHeight: 1.4 }}>
      {t('LP_Guess_HeroExpertBadgeText')}
    </p>
  </div>
);

const Section = ({ bg = '#fff', py = 64, children, style = {}, maxW = 480, px = 24 }) => (
  <section style={{ background: bg, padding: `${py}px ${px}px`, ...style }}>
    <div style={{ maxWidth: maxW, margin: '0 auto' }}>
      {children}
    </div>
  </section>
);

const Stack = ({ gap = 24, align = 'stretch', children, style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, alignItems: align, ...style }}>
    {children}
  </div>
);

const StickyBar = ({ t, K, isDesktop }) => {
  const { metaEvent } = useMeta();

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: '#103642', padding: isDesktop ? '14px 40px' : '14px 20px',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
    }}>
      <div style={{
        maxWidth: isDesktop ? 1200 : '100%',
        margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <p style={{ ...SANS, fontSize: 14, color: '#F6E147', fontWeight: 600,
          margin: 0, lineHeight: 1.3, flex: 1 }}>
          {t(K.StickyLine2)}
        </p>
        <CustomLink to="/quiz" onClick={() => metaEvent('Lead')} className="lp-sticky-cta" style={{
          ...SANS, background: '#F6E147', color: '#103642',
          fontWeight: 700, fontSize: 15, borderRadius: 999,
          padding: '12px 22px', textDecoration: 'none',
          whiteSpace: 'nowrap', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {t(K.HeroCta)} →
        </CustomLink>
      </div>
    </div>
  );
};

// ── Trust bar (new for this page: two stacked, non-conflicting proof claims) ──
const TrustBar = ({ t, isDesktop, maxW, px }) => (
  <section style={{ background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
    <div style={{ maxWidth: maxW, margin: '0 auto', padding: `20px ${px}px`,
      display: 'flex', flexDirection: isDesktop ? 'row' : 'column',
      alignItems: isDesktop ? 'center' : 'stretch',
      justifyContent: 'center', gap: isDesktop ? 32 : 12 }}>
      {[K.TrustLine1, K.TrustLine2].map((key) => (
        <div key={key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: isDesktop ? 'flex-start' : 'center', gap: 8 }}>
          <span style={{ marginTop: 2, flexShrink: 0 }}>
            <Icon name="tealCheckCircle" size={16} />
          </span>
          <p style={{ ...SANS, fontSize: 13, fontWeight: 600, color: '#103642', margin: 0, lineHeight: 1.4 }}>
            {t(key)}
          </p>
        </div>
      ))}
    </div>
  </section>
);

// ── Re-curated testimonial grid: certainty/confidence framing only ──
// Sport/recovery-tagged people (Anna, Fabienne, Stefan) dropped per ICP
// strategy (fights anti-signal, no gym/athletic framing). Quote text is
// reused verbatim from the existing on-file reviews — only the tag chip
// next to each is reframed toward confidence/consistency.
const GridCard = ({ type, file, preview, name, quote, tag }) => (
  <div style={{ flex: '0 0 220px', display: 'flex', flexDirection: 'column', gap: 10 }}>
    {type === 'video' ? (
      <video
        controls
        style={{ width: '100%', objectFit: 'cover', height: 280, borderRadius: 14, border: '1px solid #f0f0f0' }}
        poster={preview}
        playsInline
        preload={preview ? 'none' : 'metadata'}
      >
        <source src={preview ? file : `${file}#t=0.1`} type={/\.mov$/i.test(file) ? undefined : 'video/mp4'} />
      </video>
    ) : (
      <img src={file} alt="" style={{ width: '100%', objectFit: 'cover', height: 280, borderRadius: 14, border: '1px solid #f0f0f0' }} />
    )}
    <p style={{ ...SANS, fontSize: 14, fontWeight: 600, color: '#103642', margin: 0, textAlign: 'center' }}>{name}</p>
    <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.6)', margin: 0, textAlign: 'center', lineHeight: 1.5 }}>{quote}</p>
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ ...SANS, fontSize: 11, fontWeight: 600, color: '#116D6E',
        background: 'rgba(17,109,110,0.08)', border: '1px solid rgba(17,109,110,0.2)',
        borderRadius: 999, padding: '4px 10px' }}>
        {tag}
      </span>
    </div>
  </div>
);

const TestimonialGrid = ({ t }) => {
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL;
  const cards = [
    {
      type: 'video', file: `${PUBLIC_URL}resources/videos/Testimonials+/8.mp4`, preview: Preview8,
      name: 'Lisa', quote: t('BoomTestimonial8Review'), tag: t(K.GridTagClarity),
    },
    {
      type: 'video', file: `${PUBLIC_URL}resources/videos/Testimonials+/10.mp4`,
      name: 'Karin', quote: t('BoomTestimonial9Review'), tag: t(K.GridTagKnow),
    },
    {
      type: 'video', file: `${PUBLIC_URL}resources/videos/Testimonials+/3.mp4`, preview: Preview3,
      name: 'Stefan', quote: t('BoomTestimonial3Review'), tag: t(K.GridTagSecurity),
    },
    {
      type: 'video', file: `${PUBLIC_URL}resources/videos/Testimonials+/9.mp4`,
      name: 'Janine', quote: t('BoomTestimonial10Review'), tag: t(K.GridTagConsistency),
    },
    {
      type: 'image', file: `${PUBLIC_URL}resources/images/testimonials/2.webp`,
      name: 'Chris', quote: t('BoomTestimonial2Review'), tag: t(K.GridTagClarity),
    },
    {
      type: 'video', file: `${PUBLIC_URL}resources/videos/Testimonials+/1.mp4`, preview: Preview1,
      name: 'Victor R.', quote: t('BoomTestimonial1Review'), tag: t(K.GridTagTrust),
    },
    {
      type: 'image', file: `${PUBLIC_URL}resources/images/testimonials/4.webp`,
      name: 'Anna', quote: t('BoomTestimonial4Review'), tag: t(K.GridTagSecurity),
    },
    {
      type: 'image', file: `${PUBLIC_URL}resources/images/testimonials/5.webp`,
      name: 'Julien', quote: t('BoomTestimonial5Review'), tag: t(K.GridTagConsistency),
    },
    {
      type: 'image', file: FabienneImg,
      name: 'Fabienne', quote: t('BoomTestimonial6Review'), tag: t(K.GridTagTrust),
    },
  ];

  return (
    <Stack gap={20}>
      <h2 style={{ ...SERIF, fontSize: 28, lineHeight: 1.15, fontWeight: 400,
        color: '#103642', margin: 0, textAlign: 'center' }}>
        {t(K.GridHeading)}
      </h2>
      <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 4 }}>
        {cards.map((c) => <GridCard key={c.name} {...c} />)}
      </div>
    </Stack>
  );
};

// ── Comparison table: accuracy-framed (row 4 pending Philipp sign-off) ──
const ComparisonTable = ({ t, isDesktop }) => {
  const rows = [K.CompRow1, K.CompRow2, K.CompRow3, K.CompRow4];
  const rowsOther = [K.CompRow1Other, K.CompRow2Other, K.CompRow3Other, K.CompRow4Other];
  // On mobile the "Standard" column was too narrow and its (longer German) copy
  // got clipped. Give it more room, fix the layout, and let long words wrap.
  const wFeature = isDesktop ? '55%' : '40%';
  const wCol = isDesktop ? '22.5%' : '30%';
  const cellPad = isDesktop ? 12 : 9;
  const wrap = { overflowWrap: 'anywhere', wordBreak: 'break-word' };
  return (
    <Stack gap={16}>
      <h2 style={{ ...SERIF, fontSize: 28, lineHeight: 1.15, fontWeight: 400,
        color: '#103642', margin: 0, textAlign: 'center' }}>
        {t(K.ComparisonHeading)}
      </h2>
      <div style={{ borderRadius: 16, border: '1px solid #e5e5e5', overflow: 'hidden' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', background: '#fff', tableLayout: isDesktop ? 'auto' : 'fixed' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
              <th style={{ padding: cellPad, textAlign: 'left', ...SANS, fontSize: 13, fontWeight: 600, color: 'rgba(16,54,66,0.6)', width: wFeature }}>
                {t(K.CompFeature)}
              </th>
              <th style={{ padding: 12, textAlign: 'center', background: 'rgba(17,109,110,0.06)', borderLeft: '1px solid #e5e5e5', width: wCol }}>
                <img src={logo_home} alt="riise" height={22} style={{ display: 'inline-block' }} />
              </th>
              <th style={{ padding: 12, textAlign: 'center', borderLeft: '1px solid #e5e5e5', width: wCol, ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.5)' }}>
                Standard
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((rowKey, i) => (
              <tr key={rowKey} style={{ borderTop: '1px solid #e5e5e5' }}>
                <td style={{ padding: cellPad, ...SANS, fontSize: 14, fontWeight: 500, color: '#103642', ...wrap }}>{t(rowKey)}</td>
                <td style={{ padding: cellPad, textAlign: 'center', background: 'rgba(17,109,110,0.06)', borderLeft: '1px solid #e5e5e5' }}>
                  <Icon name="tealCheckCircle" size={18} />
                </td>
                <td style={{ padding: cellPad, textAlign: 'center', borderLeft: '1px solid #e5e5e5', ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.5)', ...wrap }}>
                  {t(rowsOther[i])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Stack>
  );
};

const StopGuessing = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const reviewStats = useReviewStats();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 1200
  );

  // Landing pages render outside AppLayout, so the global ?lang= handler in
  // Layout.jsx never runs and i18n stays on its init value ('en'). This is a
  // German ad LP — default to German unless the URL explicitly requests English.
  useEffect(() => {
    const urlLang = (searchParams.get('lang') || '').toLowerCase();
    const lng = urlLang === 'en' ? 'en' : 'de';
    if (i18n.language !== lng) i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxW = isDesktop ? 1200 : 480;
  const px = isDesktop ? 80 : 24;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', ...SANS, paddingBottom: 72 }}>
      <Helmet>
        <title>Schluss mit dem Raten. | riise.</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <DiscountBanner />
      <Header languageOptions={LP_LANGUAGE_OPTIONS} />
      <StickyBar t={t} K={K} isDesktop={isDesktop} />

      {/* ── Hero ── */}
      {isDesktop ? (
        <section style={{ background: '#F9F9F9', overflow: 'hidden' }}>
          <div style={{
            maxWidth: 1400, margin: '0 auto',
            display: 'grid', gridTemplateColumns: '52% 48%',
            minHeight: 600,
          }}>
            {/* Left: content */}
            <div style={{ padding: '56px 64px 56px 80px', display: 'flex', alignItems: 'center' }}>
              <Stack gap={24} style={{ width: '100%', maxWidth: 560 }}>
                <HeroExpertBadge t={t} />
                <h1 style={{ ...SERIF, fontSize: 52, lineHeight: 1.1, fontWeight: 400,
                  color: '#103642', margin: 0 }}>
                  {t(K.HeroH1Part1)}<br />
                  <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
                </h1>
                <p style={{ ...SANS, fontSize: 18, color: 'rgba(16,54,66,0.7)',
                  lineHeight: 1.7, margin: 0 }}>
                  {t(K.HeroSubline)}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#F6E147', fontSize: 17, letterSpacing: '-1px' }}>★★★★★</span>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)', margin: 0, whiteSpace: 'nowrap' }}>
                    {t(K.HeroSocialProof, {
                      rating: reviewStats?.average || '4.88',
                      count: reviewStats?.count ? reviewStats.count.toLocaleString('en') : '5,000+',
                    })}
                  </p>
                </div>
                <Stack gap={12} style={{ maxWidth: 380 }}>
                  <HomeCtaButton t={t} full={false} />
                  <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                    margin: 0, textAlign: 'center' }}>
                    {t(K.CtaSub)}
                  </p>
                </Stack>
              </Stack>
            </div>
            {/* Right: image */}
            <div style={{ position: 'relative', overflow: 'hidden', alignSelf: 'center',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={HeroImage} alt="riise personalisierter Beutel"
                style={{ width: '100%', maxWidth: 520, height: 'auto', display: 'block' }} loading="eager" />
            </div>
          </div>
        </section>
      ) : (
        <section style={{ background: '#F9F9F9' }}>
          <div style={{ maxWidth: 480, margin: '0 auto', padding: '28px 24px 40px' }}>
            <Stack gap={20}>
              <HeroExpertBadge t={t} />
              <h1 style={{ ...SERIF, fontSize: 28, lineHeight: 1.05, fontWeight: 400,
                color: '#103642', margin: 0 }}>
                {t(K.HeroH1Part1)}{' '}
                <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
              </h1>
              <p style={{ ...SANS, fontSize: 14, color: 'rgba(16,54,66,0.7)',
                lineHeight: 1.375, margin: 0 }}>
                {t(K.HeroSubline)}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#F6E147', fontSize: 15, letterSpacing: '-1px' }}>★★★★★</span>
                <p style={{ ...SANS, fontSize: 14, fontWeight: 600, color: 'rgba(16,54,66,0.6)', margin: 0, whiteSpace: 'nowrap' }}>
                  {t(K.HeroSocialProof, {
                    rating: reviewStats?.average || '4.88',
                    count: reviewStats?.count ? reviewStats.count.toLocaleString('en') : '5,000+',
                  })}
                </p>
              </div>
              <Stack gap={12}>
                <HomeCtaButton t={t} />
                <p style={{ ...SANS, fontSize: 14, color: 'rgba(16,54,66,0.4)',
                  textAlign: 'center', margin: 0 }}>
                  {t(K.CtaSub)}
                </p>
              </Stack>
            </Stack>
          </div>
          <img src={HeroImage} alt="riise personalisierter Beutel"
            style={{ width: '100%', height: 'auto', display: 'block' }} loading="eager" />
        </section>
      )}

      {/* ── Trust bar (new) ── */}
      <TrustBar t={t} isDesktop={isDesktop} maxW={maxW} px={px} />

      {/* ── Review ticker ── */}
      <div style={{ background: '#103642', padding: '8px 0', overflow: 'hidden' }}>
        <style>{`@keyframes riiseMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
        <div
          style={{ display: 'flex', whiteSpace: 'nowrap', width: 'max-content',
            animation: 'riiseMarquee 45s linear infinite' }}
        >
          {[0, 1].map((dup) => (
            <div key={dup} style={{ display: 'flex', alignItems: 'center',
              color: '#F9F9F9', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14 }}>
              {[
                t(K.TickerQuote1),
                t(K.TickerQuote2),
                t(K.TickerQuote3),
                t(K.TickerQuote4),
                <span key="rating" style={{ color: '#F6E147', fontWeight: 600 }}>
                  {t(ReviewRating, { rating: reviewStats?.average || '4.88' })}
                </span>,
              ].map((item, i) => (
                <React.Fragment key={i}>
                  <span style={{ padding: '0 16px' }}>{item}</span>
                  <span style={{ color: '#F6E147' }}>·</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Stats strip ── */}
      <section style={{ background: '#103642', padding: `56px ${px}px` }}>
        <div style={{
          maxWidth: maxW, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isDesktop ? '1fr 1fr 1fr 1fr' : '1fr 1fr',
          gap: isDesktop ? '0 40px' : '48px 0',
        }}>
          {[
            { val: K.Stat1Val, label: K.Stat1Label },
            { val: K.Stat2Val, label: K.Stat2Label },
            { val: K.Stat3Val, label: K.Stat3Label },
            {
              val: K.Stat4Val, label: K.Stat4Label,
              params: {
                rating: reviewStats?.average || '4.9',
                count: reviewStats?.count ? reviewStats.count.toLocaleString('en') : '146',
              },
            },
          ].map(({ val, label, params }) => (
            <Stack key={val} gap={8} align="center" className="lp-stat" style={{ textAlign: 'center' }}>
              <p style={{ ...SERIF, fontSize: isDesktop ? 60 : 52, lineHeight: 1, fontWeight: 400,
                color: '#F6E147', margin: 0 }}>
                {t(val, params)}
              </p>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.4, maxWidth: 140, margin: 0 }}>
                {t(label, params)}
              </p>
            </Stack>
          ))}
        </div>
      </section>

      {/* ── Problem section ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={20}>
          <Label>{t(K.ProbSectionLabel)}</Label>

          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ProbH2Line1)}<br />
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.ProbH2Line2)}</em>
          </h2>

          <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.6)',
            lineHeight: 1.7, margin: 0, textAlign: 'center',
            maxWidth: isDesktop ? 640 : undefined, alignSelf: isDesktop ? 'center' : undefined }}>
            {t(K.ProbSubline)}
          </p>

          <div style={{
            marginTop: 8,
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? '1fr 1fr' : undefined,
            flexDirection: isDesktop ? undefined : 'column',
            gap: 16,
          }}>
            {/* Old way */}
            <div className="lp-card" style={{ borderRadius: 16, padding: isDesktop ? 32 : 24, background: '#e8e4df' }}>
              <p style={{ ...SANS, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'rgba(16,54,66,0.5)',
                margin: '0 0 20px 0' }}>
                {t(K.OldWayTitle)}
              </p>
              <Stack gap={16}>
                {[K.OldWay1, K.OldWay2, K.OldWay3, K.OldWay4, K.OldWay5].map((key) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                      fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✕</span>
                    <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                      margin: 0, lineHeight: 1.5 }}>{t(key)}</p>
                  </div>
                ))}
              </Stack>
            </div>

            {/* riise way */}
            <div className="lp-card lp-card-riise" style={{ borderRadius: 16, padding: isDesktop ? 32 : 24, background: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid rgba(17,109,110,0.1)' }}>
              <p style={{ ...SANS, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: '#103642', margin: '0 0 20px 0' }}>
                {t(K.NewWayTitle)}
              </p>
              <Stack gap={16}>
                {[K.NewWay1, K.NewWay2, K.NewWay3, K.NewWay4, K.NewWay5].map((key) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flexShrink: 0, marginTop: 2 }}>
                      <Icon name="tealCheckCircle" size={18} />
                    </div>
                    <p style={{ ...SANS, fontSize: 15, color: '#103642',
                      margin: 0, lineHeight: 1.5 }}>{t(key)}</p>
                  </div>
                ))}
              </Stack>
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── How it works ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={isDesktop ? 48 : 40}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.HowWorksHeading)}
          </h2>

          {isDesktop ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 32 }}>
              {[
                { n: 1, title: K.Step1Title, desc: K.Step1Desc },
                { n: 2, title: K.Step2Title, desc: K.Step2Desc },
                { n: 3, title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ n, title, desc }) => (
                <div key={n} className="lp-card" style={{
                  background: '#fff', borderRadius: 16, padding: 28,
                  display: 'flex', flexDirection: 'column', gap: 16,
                }}>
                  <StepNumber n={n} />
                  <p style={{ ...SANS, fontSize: 17, fontWeight: 600,
                    color: '#103642', margin: 0 }}>
                    {t(title)}
                  </p>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                    margin: 0, lineHeight: 1.65 }}>
                    {t(desc)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <Stack gap={32}>
              {[
                { n: 1, title: K.Step1Title, desc: K.Step1Desc },
                { n: 2, title: K.Step2Title, desc: K.Step2Desc },
                { n: 3, title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ n, title, desc }) => (
                <div key={n} style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                  <StepNumber n={n} />
                  <Stack gap={8}>
                    <p style={{ ...SANS, fontSize: 16, fontWeight: 600,
                      color: '#103642', margin: 0 }}>
                      {t(title)}
                    </p>
                    <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                      margin: 0, lineHeight: 1.65 }}>
                      {t(desc)}
                    </p>
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      </Section>

      {/* ── Expert (Philipp) — moved up: the resolution mechanism for this angle ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ExpertHeading)}
          </h2>
          <ExpertSection />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 450 }}>
              <PractitionerBadge />
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── Results ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={20}>
          <Label pill>{t(K.ResultsSectionLabel)}</Label>

          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ResultsH1)}<br />
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.ResultsH2)}</em>
          </h2>

          <div style={{
            marginTop: 12,
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? '1fr 1fr' : undefined,
            flexDirection: isDesktop ? undefined : 'column',
            gap: isDesktop ? 48 : 32,
          }}>
            {[
              { val: K.ResultsStat1Val, desc: K.ResultsStat1Desc },
              { val: K.ResultsStat2Val, desc: K.ResultsStat2Desc },
            ].map(({ val, desc }) => (
              <div key={val} style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <p style={{ ...SERIF, fontSize: isDesktop ? 80 : 68, lineHeight: 1, fontWeight: 400,
                  color: '#103642', margin: 0, flexShrink: 0,
                  width: isDesktop ? 180 : 150, textAlign: 'right' }}>
                  {t(val)}
                </p>
                <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.7)',
                  margin: 0, lineHeight: 1.5 }}>
                  {t(desc)}
                </p>
              </div>
            ))}
          </div>

          <p style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.3)',
            textAlign: 'center', margin: 0, marginTop: 8 }}>
            {t(K.ResultsSource)}
          </p>
        </Stack>
      </Section>

      {/* ── Solution reveal / ingredient preview ── */}
      <section style={{ background: '#fff', paddingTop: 64, paddingBottom: 32 }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `0 ${px}px` }}>
          {isDesktop ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 48, marginBottom: 48 }}>
              <img src={RiisePouch} alt="riise sachet"
                style={{ width: 140, height: 'auto', flexShrink: 0 }} />
              <div>
                <h2 style={{ ...SERIF, fontSize: 36, lineHeight: 1.15, fontWeight: 400,
                  color: '#103642', margin: '0 0 12px 0' }}>
                  {t(K.IngrHeading)}
                </h2>
                <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.5)',
                  margin: 0, lineHeight: 1.6 }}>
                  {t(K.IngrSubline)}
                </p>
              </div>
            </div>
          ) : (
            <Stack gap={24} align="center" style={{ textAlign: 'center', marginBottom: 36 }}>
              <img src={RiisePouch} alt="riise sachet"
                style={{ width: 150, height: 'auto' }} />
              <h2 style={{ ...SERIF, fontSize: 28, lineHeight: 1.15, fontWeight: 400,
                color: '#103642', margin: 0 }}>
                {t(K.IngrHeading)}
              </h2>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.5)',
                margin: 0, lineHeight: 1.6 }}>
                {t(K.IngrSubline)}
              </p>
            </Stack>
          )}
        </div>
        <IngredientsSlider />
      </section>

      {/* ── Mid-page CTA ── */}
      <Section bg="#103642" maxW={maxW} px={px}>
        <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#fff', margin: 0 }}>
            {t(K.FinalHeading)}
          </h2>
          <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.65)',
            margin: 0, lineHeight: 1.7, maxWidth: isDesktop ? 560 : undefined }}>
            {t(K.FinalSubline)}
          </p>
          <div style={{ width: isDesktop ? 'auto' : '100%' }}>
            <CtaButton full={!isDesktop}>{t(K.HeroCta)}</CtaButton>
          </div>
        </Stack>
      </Section>

      {/* ── Featured testimonial (Lisa) ── */}
      <Section bg="#fff" py={56} maxW={isDesktop ? 800 : 480} px={px}>
        <Stack gap={20}>
          <StarRow count={5} />
          <p style={{ ...SANS, fontSize: isDesktop ? 22 : 18, lineHeight: 1.6, fontWeight: 400,
            color: '#103642', margin: 0, fontStyle: 'italic' }}>
            "{t(K.FeatQuote)}"
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={Preview8} alt="Lisa"
              style={{ width: 48, height: 48, borderRadius: '50%',
                objectFit: 'cover', flexShrink: 0 }} />
            <p style={{ ...SANS, fontSize: 14, fontWeight: 600,
              color: 'rgba(16,54,66,0.6)', margin: 0 }}>
              {t(K.FeatAuthor)}
            </p>
          </div>
        </Stack>
      </Section>

      {/* ── Testimonial grid (re-curated) ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <TestimonialGrid t={t} />
      </Section>

      {/* ── Comparison table ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <ComparisonTable t={t} isDesktop={isDesktop} />
      </Section>

      {/* ── FAQ ── */}
      <Section bg="#f5f7f7" maxW={isDesktop ? 800 : 480} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0 }}>
            {t(K.FaqHeading)}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column',
            borderTop: '1px solid #e5e5e5' }}>
            {[
              { q: K.Faq1Q, a: K.Faq1A },
              { q: K.Faq2Q, a: K.Faq2A },
              { q: K.Faq3Q, a: K.Faq3A },
              { q: K.Faq4Q, a: K.Faq4A },
              { q: K.Faq5Q, a: K.Faq5A },
            ].map(({ q, a }) => (
              <div key={q} className="lp-faq-row" style={{ borderBottom: '1px solid #e5e5e5', padding: '4px 0' }}>
                <Accordion heading={t(q)}>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.7)',
                    lineHeight: 1.65, margin: '12px 0 16px 0' }}>
                    {t(a)}
                  </p>
                </Accordion>
              </div>
            ))}
          </div>
        </Stack>
      </Section>

      {/* ── Final CTA (same as home page) ── */}
      <MillionCombinations dark />

      {/* ── Reviews carousel ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ReviewsHeading)}
          </h2>
          <ReviewsMobile />
        </Stack>
      </Section>

      {/* ── Footer ── */}
      <footer style={{ background: '#fff', borderTop: '1px solid #f0f0f0',
        padding: isDesktop ? '40px 80px' : '40px 24px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={16} align="center">
            <img src={logo_home} alt="riise"
              style={{ height: 20, width: 'auto', opacity: 0.4 }} />
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              <CustomLink to="/impressum"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Impressum
              </CustomLink>
              <CustomLink to="/policy"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Privacy Policy
              </CustomLink>
            </div>
            <p style={{ ...SANS, fontSize: 11, color: 'rgba(16,54,66,0.3)',
              textAlign: 'center', margin: 0 }}>
              {t(K.AdDisclosure)} · {t(K.FoodNote)}
            </p>
          </Stack>
        </div>
      </footer>
    </div>
  );
};

export default StopGuessing;
